import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Platform } from 'react-native';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface TypingIndicatorProps {
  isVisible: boolean;
  userName?: string;
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  isVisible,
  userName
}) => {
  const styles = useThemedStyles(getStyles);
  const dot1Anim = useRef(new Animated.Value(0.4)).current;
  const dot2Anim = useRef(new Animated.Value(0.4)).current;
  const dot3Anim = useRef(new Animated.Value(0.4)).current;

  const { isRTL, locale } = useLocale();
  const t = createTranslator(locale);
  const typo = useTypography();
  const rtl = useRTLStyles();

  useEffect(() => {
    if (isVisible) {
      const createAnimation = (animValue: Animated.Value, delay: number) => {
        return Animated.loop(
          Animated.sequence([
            Animated.timing(animValue, {
              toValue: 1,
              duration: 600,
              delay,
              useNativeDriver: true,
            }),
            Animated.timing(animValue, {
              toValue: 0.4,
              duration: 600,
              useNativeDriver: true,
            }),
          ])
        );
      };

      const animation1 = createAnimation(dot1Anim, 0);
      const animation2 = createAnimation(dot2Anim, 200);
      const animation3 = createAnimation(dot3Anim, 400);

      Animated.parallel([animation1, animation2, animation3]).start();
    } else {
      // Reset animations when not visible
      dot1Anim.setValue(0.4);
      dot2Anim.setValue(0.4);
      dot3Anim.setValue(0.4);
    }
  }, [isVisible, dot1Anim, dot2Anim, dot3Anim]);


  if (!isVisible) return null;

  const containerAlign = (isRTL && Platform.OS !== 'web') ? 'flex-end' : 'flex-start';
  const bubbleBorderStyle = isRTL
    ? { borderBottomLeftRadius: 18, borderBottomRightRadius: 4 }
    : { borderBottomLeftRadius: 4, borderBottomRightRadius: 18 };

  return (
    <View style={[styles.container, { alignItems: containerAlign }]}>
      <View style={[styles.bubble, { flexDirection: rtl.rowDirection }, bubbleBorderStyle]}>
        <View style={[styles.dotsContainer, rtl.marginEnd(spacing.sm)]}>
          <Animated.View
            style={[
              styles.dot,
              { opacity: dot1Anim }
            ]}
          />
          <Animated.View 
            style={[
              styles.dot, 
              { opacity: dot2Anim }
            ]} 
          />
          <Animated.View 
            style={[
              styles.dot, 
              { opacity: dot3Anim }
            ]} 
          />
        </View>
        <Text style={[styles.text, { fontFamily: typo.fontFamily.medium }]}>
          {t('chat.isTyping', { name: userName ?? '' })}
        </Text>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    alignItems: 'flex-start',
  },
  bubble: {
    backgroundColor: colors.background.surface,
    borderRadius: 18,
    borderBottomLeftRadius: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    maxWidth: '80%',
    flexDirection: 'row',
    alignItems: 'center',
    // Subtle shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    borderWidth: 0.5,
    borderColor: '#E5E5E5',
  },
  dotsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary,
    marginHorizontal: 1,
  },
  text: {
    fontSize: 13,
    color: '#666666',
    fontFamily: typography.fontFamily.medium,
    fontStyle: 'italic',
  },
});
